import { useEffect, useState } from "react";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { enqueueSnackbar } from "../../../components/snackbar";
import FormProvider from "../../../components/hook-form";
import FramesAndLenses from "./Specs/FramesAndLenses";
import AddonAndVoucher from "./Specs/AddonAndVoucher";
import FinalSummary from "../Patient/Refraction/FinalSummary";
import { IDispenseRequest } from "../../../@types/dispense";
import { Addon } from "../../../@types/addon.type";
import { Vouchers } from "../../../@types/voucher.type";
import { DispenseService } from "../../../api/dispense.service";
import DispenseTable from "./DispenseTable";
import { useAppContext } from "../../../providers/AppContext";
import useBarcodeScanner from "../../../hooks/useBarcodeScanner";
import { ProductService } from "../../../api/product.service";
import AddPatient from "../../../sections/@dashboard/patient/AddPatient";
import ConfirmDialog from "../../../components/confirm-dialog";

export default function DispensePage() {
  const { dispenseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [addons, setAddons] = useState<Addon[]>([]);
  const [vouchers, setVouchers] = useState<Vouchers[]>([]);
  const [date, setDate] = useState<Date | null>(new Date());
  const [time, setTime] = useState<string | null>(
    new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  );
  const [dispenseData, setDispenseData] = useState<any>(null);
  const [selectedDispense, setSelectedDispense] = useState<any>(null);
  const [selectedPatient, setSelectedPatient] = useState<
    number | string | null
  >(null);
  const [manualDiscount, setManualDiscount] = useState<number>(0);
  const [openPatientModal, setOpenPatientModal] = useState(false);
  const [confirmClosePatientForm, setConfirmClosePatientForm] = useState(false);

  const state = location.state as any;

  const methods = useForm<IDispenseRequest>({
    mode: "onChange",
    defaultValues: {
      patientId: null,
      type: "",
      specType: "",
      pdRight: null,
      pdLeft: null,
      heightRight: null,
      heightLeft: null,
      bvd: null,
      pantoscopicTilt: null,
      wrapAngle: null,
      adjustedPrice: 0,
      totalPrice: 0,
      discount: 0,
      pickupDateTime: "",
      framesAndLenses: [],
      addons: [],
      vouchers: [],
      orderingNotes: "",
      handoverNotes: "",
      hobbies: "",
      isVDU: false,
      isSmoker: false,
      isDriver: false,
      isExternal: false,
      sourceType: state?.sourceType || undefined,
      sourceId: state?.sourceId || undefined,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "framesAndLenses",
  });

  const watchedIsExternal = useWatch({ control, name: "isExternal" });

  const watchedFramesAndLenses = useWatch({ control, name: "framesAndLenses" });

  const calculateFramesAndLensesPrice = () => {
    if (!watchedFramesAndLenses) return 0;
    return watchedFramesAndLenses.reduce((total: number, item: any) => {
      const price = parseFloat(item.price) || 0;
      return total + price;
    }, 0);
  };

  const framesAndLensesPrice = calculateFramesAndLensesPrice();

  const { patients, getPatients } = useAppContext();

  const isNew = dispenseId === "new" || !dispenseId;

  const [allProducts, setAllProducts] = useState<any[]>([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await new ProductService().getAllProducts();
        setAllProducts(data);
      } catch (error) {
        console.error("Failed to fetch products for scanner", error);
      }
    };
    fetchProducts();
  }, []);

  const handleScan = (barcode: string) => {
    const product = allProducts.find(
      (p) => p.code?.toLowerCase() === barcode.toLowerCase(),
    );

    if (product) {
      (append as any)({
        id: product.id,
        name: product.name,
        type: product.type,
        price: product.salePrice ?? product.price ?? 0,
        category: product.category,
        code: product.code,
        quantity: 1,
        differentRL: false,
      });
      enqueueSnackbar(`Added ${product.name}`, { variant: "success" });
    } else {
      enqueueSnackbar(`Product with code "${barcode}" not found`, {
        variant: "warning",
      });
    }
  };

  useBarcodeScanner({ onScan: handleScan });

  useEffect(() => {
    const state = location.state as any;
    if (isNew && state?.patientId) {
      setSelectedPatient(state.patientId);
    }
    if (isNew) getPatients();
  }, [isNew, location.state]);

  const onSubmit = async (data: IDispenseRequest) => {
    if (!data.patientId) {
      enqueueSnackbar("Please select a patient", { variant: "error" });
      return;
    }
    if (!data.type) {
      enqueueSnackbar("Please select frames or contact lenses type", {
        variant: "error",
      });
      return;
    }
    if (!data.specType) {
      enqueueSnackbar("Please select spec type", { variant: "error" });
      return;
    }
    if (!data.pdRight || !data.pdLeft) {
      enqueueSnackbar("Please fill pupillary distance for both eyes", {
        variant: "error",
      });
      return;
    }
    if (!date || !time) {
      enqueueSnackbar("Please select pickup date and time", {
        variant: "error",
      });
      return;
    }
    if (data.framesAndLenses.length === 0) {
      enqueueSnackbar("Please add at least one frame and lens", {
        variant: "error",
      });
      return;
    }

    if (["Varifocal", "Bifocal"].includes(data.specType)) {
      if (!data.heightRight || !data.heightLeft) {
        enqueueSnackbar(
          "Height measurements are required for Varifocal/Bifocal lenses",
          {
            variant: "error",
          },
        );
        return;
      }
    }

    for (let i = 0; i < data.framesAndLenses.length; i++) {
      const item = data.framesAndLenses[i];
      if (!item.name || !item.type || !item.price || !item.category) {
        enqueueSnackbar(`Please fill all fields for frame/lens item ${i + 1}`, {
          variant: "error",
        });
        return;
      }
    }

    // Inside onSubmit, as the first check:
    const currentDiscount = methods.getValues("discount");
    const currentTotal = methods.getValues("totalPrice");
    if (currentDiscount && currentTotal) {
      if (currentDiscount > currentTotal) {
        enqueueSnackbar("Discount cannot exceed the total price", {
          variant: "error",
        });
        return;
      }
    }
    const framesAndLensesIds = data.framesAndLenses
      .map((item: any) => item.id)
      .filter((id: string) => !!id);

    const addonIds = data.addons
      .map((item: any) => Number(item.addon))
      .filter((id: number) => !isNaN(id) && id !== 0);

    const voucherIds = data.vouchers
      .map((item: any) => Number(item.voucher))
      .filter((id: number) => !isNaN(id) && id !== 0);

    const pickupDateTime =
      date && time
        ? new Date(
            `${date.toISOString().split("T")[0]}T${time}:00`,
          ).toISOString()
        : "";

    const dispensePayload = {
      ...data,
      patientId: data.patientId,
      status: "draft",
      framesAndLenses: framesAndLensesIds,
      addons: addonIds,
      vouchers: voucherIds,
      pickupDateTime,

      pdRight: data.pdRight ? Number(data.pdRight) : null,
      pdLeft: data.pdLeft ? Number(data.pdLeft) : null,
      heightRight: data.heightRight ? Number(data.heightRight) : null,
      heightLeft: data.heightLeft ? Number(data.heightLeft) : null,
      bvd: data.bvd ? Number(data.bvd) : null,
      pantoscopicTilt: data.pantoscopicTilt
        ? Number(data.pantoscopicTilt)
        : null,
      wrapAngle: data.wrapAngle ? Number(data.wrapAngle) : null,
    };

    try {
      let response: any;
      if (selectedDispense?.id) {
        response = await new DispenseService().updateDispense(
          selectedDispense.id,
          dispensePayload as any,
        );
        console.log("RESPONSE", response);
        enqueueSnackbar(
          "Dispense Updated Successfully!.... Redirecting to Till",
          {
            variant: "success",
          },
        );
      } else {
        response = await new DispenseService().createDispense(
          dispensePayload as any,
        );
        console.log("RESPONSE", response);
        enqueueSnackbar(
          "Draft Dispense and Till Created Successfully!.... Redirecting to Till",
          {
            variant: "success",
          },
        );
      }

      setTimeout(() => {
        // Check if response data exists and has till id, or maybe just reload/navigate
        const tillId = response?.data?.till?.id || response?.data?.id; // fallback if structure differs
        // If update returns dispense but not till explicitly, we might need to handle it.
        // updateDispense backend returns { ...dispense }, but create returns { dispense, till }.
        // Wait, backend updateDispense returns `this.findOne(id)` which returns dispense entity.
        // It does NOT return { till }.
        // But the redirection logic expects `response.data.till.id`.
        // If we update, the till likely already exists. We should probably fetch it or just navigate back.
        // Or we can just navigate to dispense list.

        // For now, let's just use what we have. If till ID is missing, maybe navigate to dispense list?
        if (response?.data?.till?.id) {
          navigate(`/till/${response.data.till.id}`);
        } else {
          // If update, we might stay or go somewhere else.
          // The original code navigated to till.
          // Let's assume for update we might want to go to till too, but we need till ID.
          // The backend updateDispense doesn't return till.
          // I should fix backend updateDispense to return till if possible, or frontend to find it.
          // Actually, for now, let's just navigate to dispense list or stay.
          // Or, we can fetch till for this dispense.
          navigate(-1); // Go back?
        }
      }, 2000);
    } catch (err: any) {
      console.error("Error creating dispense:", err);

      let message = "Error creating dispense";

      if (err.response?.data) {
        const res = err.response.data;

        if (Array.isArray(res.message) && res.message.length > 0) {
          message = res.message[0];
        } else if (typeof res.message === "string") {
          message = res.message;
        }
      } else if (err.message) {
        message = err.message[0];
      }

      enqueueSnackbar(message, {
        variant: "error",
      });
    }
  };

  const handleExternalPatientAdded = (newPatient: any) => {
    // response.data shape from createPatient — adjust key if your API wraps differently
    const patient = newPatient?.data ?? newPatient;
    if (!patient?.id) return;

    setSelectedPatient(patient.id);
    methods.setValue("patientId", patient.id, { shouldDirty: true });
    setOpenPatientModal(false);
  };

  const fetchDispenseData = async () => {
    if (isNew) {
      reset();
      setDispenseData(null);
      setSelectedDispense(null);
      return;
    }

    try {
      const res = await new DispenseService().getDispense(+dispenseId);
      setDispenseData(res.data);

      if (res.data.dispenses?.length > 0) {
        setSelectedDispense(res.data.dispenses[0]);
      }
    } catch (error) {
      enqueueSnackbar("Failed to fetch dispense data.", { variant: "error" });
      console.error(error);
    }
  };

  function mapDispenseToForm(dispense: any) {
    const voucherDiscount =
      dispense.vouchers?.reduce(
        (sum: number, v: any) => sum + (v.price || 0),
        0,
      ) || 0;

    const manualDiscount = Math.max(
      0,
      (dispense.discount || 0) - voucherDiscount,
    );

    return {
      formValues: {
        patientId: dispense.patientId ?? "",
        type: dispense.type ?? "",
        specType: dispense.specType ?? "",
        pdRight: dispense.pdRight ?? null,
        pdLeft: dispense.pdLeft ?? null,
        heightRight: dispense.heightRight ?? null,
        heightLeft: dispense.heightLeft ?? null,
        bvd: dispense.bvd ?? null,
        pantoscopicTilt: dispense.pantoscopicTilt ?? null,
        wrapAngle: dispense.wrapAngle ?? null,
        pickupDateTime: dispense.pickupDateTime ?? "",
        adjustedPrice: dispense.adjustedPrice ?? 0,
        totalPrice: dispense.totalPrice ?? 0,
        discount: dispense.discount ?? 0,
        framesAndLenses:
          dispense.products?.map((p: any) => ({
            id: p.id,
            name: p.name,
            type: p.type,
            price: p.salePrice ?? p.price ?? 0,
            category: p.category,
          })) ?? [],
        addons:
          dispense.addons?.map((a: any) => ({
            addon: a.id,
            discount: 0,
          })) ?? [],
        vouchers:
          dispense.vouchers?.map((v: any) => ({
            voucher: v.id,
            discount: v.price,
          })) ?? [],
        orderingNotes: dispense.orderingNotes ?? "",
        handoverNotes: dispense.handoverNotes ?? "",
        hobbies:
          dispense.hobbies ??
          dispense.clinicalRecord?.prescreening?.hobbies ??
          "",
        isDriver:
          dispense.isDriver ??
          dispense.clinicalRecord?.prescreening?.isDriver ??
          false,
        isVDU:
          dispense.isVDU ??
          dispense.clinicalRecord?.prescreening?.isVduUse ??
          false,
        isSmoker:
          dispense.isSmoker ??
          dispense.clinicalRecord?.prescreening?.isSmoker ??
          false,
        isExternal: dispense.isExternal ?? false,
      },
      manualDiscount,
    };
  }

  function extractDateTime(pickupDateTime?: string) {
    if (!pickupDateTime) return null;

    const d = new Date(pickupDateTime);

    return {
      date: d,
      time: d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    };
  }

  const handlePatientAdded = (newPatient: any) => {
    if (!newPatient?.id) return;
    setSelectedPatient(newPatient.id);
    methods.setValue("patientId", newPatient.id, { shouldDirty: true });
    // Refresh patients list so the new one appears in the dropdown
    getPatients();
    setOpenPatientModal(false);
  };

  useEffect(() => {
    fetchDispenseData();
  }, [dispenseId]);

  useEffect(() => {
    if (!selectedDispense) return;

    const dateTime = extractDateTime(selectedDispense.pickupDateTime);
    if (dateTime) {
      setDate(dateTime.date);
      setTime(dateTime.time);
    }

    const { formValues, manualDiscount } = mapDispenseToForm(selectedDispense);

    setManualDiscount(manualDiscount);
    reset(formValues);
  }, [selectedDispense, reset]);

  useEffect(() => {
    const fetchPatientDispenses = async () => {
      if (!selectedPatient) return;

      try {
        const res = await new DispenseService().getDispenseByPatientId(
          selectedPatient,
        );

        if (res.data && (res.data.dispenses || res.data.patient)) {
          setDispenseData(res.data);
        } else {
          setDispenseData(null);
          return;
        }

        reset({
          patientId: selectedPatient,
          type: "",
          specType: "",
          pdRight: null,
          pdLeft: null,
          heightRight: null,
          heightLeft: null,
          bvd: null,
          pantoscopicTilt: null,
          wrapAngle: null,
          adjustedPrice: 0,
          totalPrice: 0,
          discount: 0,
          pickupDateTime: "",
          framesAndLenses: [],
          addons: [],
          vouchers: [],
          orderingNotes: "",
          handoverNotes: "",
          hobbies: "",
          isVDU: false,
          isSmoker: false,
          isDriver: false,
          isExternal: false,
          sourceType: state?.sourceType || undefined,
          sourceId: state?.sourceId || undefined,
        });
      } catch (error: any) {
        const isNoDispenseError =
          error.response?.status === 404 ||
          error.message?.includes("not found") ||
          error.message?.includes("No dispense");

        if (!isNoDispenseError) {
          enqueueSnackbar("Failed to fetch patient dispense data.", {
            variant: "error",
          });
          console.error(error);
        } else {
          enqueueSnackbar("Patient has no existing dispenses", {
            variant: "warning",
          });
          setDispenseData(null);
        }
      }
    };

    if (isNew && selectedPatient) {
      fetchPatientDispenses();
    }
  }, [isNew, selectedPatient, reset]);

  useEffect(() => {
    if (watchedIsExternal) {
      setOpenPatientModal(true);
    } else {
      setSelectedPatient(null);
      methods.setValue("patientId", null);
    }
  }, [watchedIsExternal]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h4" sx={{ p: 1, mb: 4 }}>
        Add New Dispense
      </Typography>
      {dispenseData && (
        <DispenseTable
          dispenseData={dispenseData}
          selectedDispense={selectedDispense}
          onSelect={setSelectedDispense}
        />
      )}
      <Box sx={{ px: 1, width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "start", width: "100%" }}>
          <Box
            sx={{
              height: "50px",
              display: "flex",
              alignItems: "center",
              mr: 2,
              mt: "2px",
            }}>
            <Typography variant="h5">Info</Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                width: "130%",
              }}>
              <Controller
                name="patientId"
                control={control}
                rules={{ required: "Patient selection is required" }}
                render={({ field }) => (
                  <TextField
                    select
                    label="Patient"
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      setSelectedPatient(value);
                    }}
                    value={field.value || ""}
                    error={!!errors.patientId}
                    helperText={errors.patientId?.message}
                    required>
                    {isNew
                      ? patients.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {`${p.name}`}
                          </MenuItem>
                        ))
                      : dispenseData?.patient && (
                          <MenuItem value={dispenseData.patient.id}>
                            {`${dispenseData.patient.firstName} ${dispenseData.patient.surName}`}
                          </MenuItem>
                        )}
                  </TextField>
                )}
              />
              <Box
                sx={{
                  width: "100%",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  mr: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  px: "12px",
                }}>
                <Controller
                  name="isVDU"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Is VDU"
                      labelPlacement="start"
                    />
                  )}
                />
                <Controller
                  name="isSmoker"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Is Smoker"
                      labelPlacement="start"
                    />
                  )}
                />
                <Controller
                  name="isDriver"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Is Driver"
                      labelPlacement="start"
                    />
                  )}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                width: "70%",
              }}>
              <Controller
                name="type"
                control={control}
                rules={{ required: "Type selection is required" }}
                render={({ field }) => (
                  <TextField
                    select
                    label="Frames or Contact Lenses"
                    {...field}
                    value={field.value || ""}
                    error={!!errors.type}
                    helperText={errors.type?.message}
                    required>
                    <MenuItem value="dispense_frames">Dispense Frames</MenuItem>
                    <MenuItem value="dispense_contact_lenses">
                      Dispense Contact Lenses
                    </MenuItem>
                  </TextField>
                )}
              />
              <Controller
                name="hobbies"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Hobbies"
                    size="small"
                    {...field}
                    value={field.value || ""}
                  />
                )}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              width: "15%",
              mt: 1,
            }}>
            <Controller
              name="isExternal"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch checked={field.value} onChange={field.onChange} />
                  }
                  label="Is External"
                  labelPlacement="start"
                />
              )}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ width: "100%", mt: 2, px: 1 }}>
        <FinalSummary
          finalData={selectedDispense?.clinicalRecord?.refraction?.final || {}}
        />
      </Box>
      <Box
        sx={{
          px: 1,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          width: "100%",
          justifyContent: "space-between",
        }}>
        <Typography variant="h6">Notes</Typography>
        <Controller
          name="handoverNotes"
          control={control}
          render={({ field }) => (
            <TextField
              label="Handover Notes"
              {...field}
              value={field.value || ""}
            />
          )}
        />
        <Controller
          name="orderingNotes"
          control={control}
          render={({ field }) => (
            <TextField
              label="Ordering Notes"
              multiline
              rows={4}
              {...field}
              value={field.value || ""}
            />
          )}
        />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", mt: 4, px: 1 }}>
        <Typography variant="h6">Pupilary distance</Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            width: "100%",
            mt: 4,
          }}>
          <Box
            sx={{ display: "flex", alignItems: "center", mr: 4, width: "40%" }}>
            <Typography sx={{ mr: 2 }}>Right</Typography>
            <Controller
              name="pdRight"
              control={control}
              rules={{
                min: {
                  value: 20,
                  message: "PD must be at least 20 mm",
                },
                max: {
                  value: 40,
                  message: "PD cannot exceed 40 mm",
                },
              }}
              render={({ field }) => (
                <TextField
                  type="number"
                  size="small"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(e) => {
                    const input = e.target.value;

                    const value: number | null =
                      input === "" ? null : Number(input);

                    field.onChange(value);

                    methods.setValue("pdLeft", value);
                  }}
                  error={!!errors.pdRight}
                  helperText={errors.pdRight?.message}
                  required
                />
              )}
            />
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "center", mr: 4, width: "40%" }}>
            <Typography sx={{ mr: 2 }}>Left</Typography>
            <Controller
              name="pdLeft"
              control={control}
              rules={{
                min: {
                  value: 20,
                  message: "PD must be at least 20 mm",
                },
                max: {
                  value: 40,
                  message: "PD cannot exceed 40 mm",
                },
              }}
              render={({ field }) => (
                <TextField
                  type="number"
                  size="small"
                  {...field}
                  value={field.value ?? ""} // null → ""
                  onChange={(e) => {
                    const input = e.target.value;
                    const value: number | null =
                      input === "" ? null : Number(input);
                    field.onChange(value);
                  }}
                  error={!!errors.pdLeft}
                  helperText={errors.pdLeft?.message}
                  required
                />
              )}
            />
          </Box>
          <Controller
            name="specType"
            control={control}
            rules={{ required: "Spec type selection is required" }}
            render={({ field }) => (
              <TextField
                select
                label="Choose Specs Type"
                {...field}
                value={field.value || ""}
                sx={{ minWidth: 200 }}
                error={!!errors.specType}
                helperText={errors.specType?.message}
                required>
                <MenuItem value="Distance">Distance</MenuItem>
                <MenuItem value="Near">Near</MenuItem>
                <MenuItem value="Intermediate">Intermediate</MenuItem>
                <MenuItem value="Bifocal">Bifocal</MenuItem>
                <MenuItem value="Varifocal">Varifocal</MenuItem>
              </TextField>
            )}
          />
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", mt: 4, px: 1 }}>
        <Typography variant="h6">Additional Measurements</Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            width: "100%",
            mt: 2,
            flexWrap: "wrap",
          }}>
          <Controller
            name="heightRight"
            control={control}
            rules={{
              min: {
                value: 10,
                message: "Height must be at least 10 mm",
              },
              max: {
                value: 30,
                message: "Height cannot exceed 30 mm",
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                type="number"
                label="Right Height"
                size="small"
                {...field}
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                inputProps={{ max: 300 }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          <Controller
            name="heightLeft"
            control={control}
            rules={{
              min: {
                value: 10,
                message: "Height must be at least 10 mm",
              },
              max: {
                value: 30,
                message: "Height cannot exceed 30 mm",
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                type="number"
                label="Left Height"
                size="small"
                {...field}
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                inputProps={{ max: 300 }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          {/* <Controller
            name="bvd"
            control={control}
            render={({ field }) => (
              <TextField
                type="number"
                label="BVD"
                size="small"
                {...field}
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(e.target.value === "" ? null : Number(e.target.value))
                }
              />
            )}
          />
          <Controller
            name="pantoscopicTilt"
            control={control}
            render={({ field }) => (
              <TextField
                type="number"
                label="Pantoscopic Tilt"
                size="small"
                {...field}
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(e.target.value === "" ? null : Number(e.target.value))
                }
              />
            )}
          />
          <Controller
            name="wrapAngle"
            control={control}
            render={({ field }) => (
              <TextField
                type="number"
                label="Wrap Angle"
                size="small"
                {...field}
                value={field.value ?? ""}
                onChange={(e) =>
                  field.onChange(e.target.value === "" ? null : Number(e.target.value))
                }
              />
            )}
          /> */}
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row", mt: 2 }}>
        <FramesAndLenses
          specType={"specType"}
          fields={fields}
          append={append}
          remove={remove}
          disabled={false}
        />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <AddonAndVoucher
          control={control}
          addons={addons}
          vouchers={vouchers}
          setAddons={setAddons}
          setVouchers={setVouchers}
          disabled={false}
          date={date}
          setDate={setDate}
          time={time}
          setTime={setTime}
          framesAndLensesPrice={framesAndLensesPrice}
          initialManualDiscount={manualDiscount}
        />
      </Box>{" "}
      <Box
        sx={{ display: "flex", flexDirection: "row-reverse", width: "100%" }}>
        <LoadingButton type="submit" variant="outlined" sx={{ my: 2, mx: 1 }}>
          Save Draft
        </LoadingButton>
        {/* <LoadingButton type="button" variant="outlined" sx={{ my: 2, mx: 1 }} disabled={!isValid}> */}
        {/* <LoadingButton type="button" variant="outlined" sx={{ my: 2, mx: 1 }}>
          Send to Till
        </LoadingButton> */}
      </Box>
      <Dialog
        fullWidth
        maxWidth="md"
        open={openPatientModal}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            setConfirmClosePatientForm(true);
          } else {
            setOpenPatientModal(false);
          }
        }}>
        <DialogTitle>Add Patient</DialogTitle>
        <AddPatient
          onSubmitForm={handleExternalPatientAdded}
          Fields={false}
          isEdit={false}
          onCancel={() => {
            // Cancelling without saving → reset the toggle too
            setOpenPatientModal(false);
            methods.setValue("isExternal", false);
          }}
        />
      </Dialog>
      <ConfirmDialog
        open={confirmClosePatientForm}
        onClose={() => setConfirmClosePatientForm(false)}
        title="Discard form?"
        content={
          <>It will erase all the data you entered. Do you want to continue?</>
        }
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setConfirmClosePatientForm(false);
              setOpenPatientModal(false);
              methods.setValue("isExternal", false); // no patient added, reset toggle
            }}>
            Yes
          </Button>
        }
      />
    </FormProvider>
  );
}
